import { storageKeys, timing } from "../config";
/**
 * WalletConnect v2: the way in from a phone, or from a browser with no
 * extension installed.
 *
 * Everything here is lazy. `@walletconnect/ethereum-provider` drags in a large
 * chunk graph and must never sit in the boot bundle, so it is reached only
 * through the `await import()` in `defaultFactory`. Nothing is created until
 * the operator picks WalletConnect (or, on boot, unless they picked it last
 * time).
 *
 * What it produces is a plain EIP-1193 object, so `wallet.ts` drives it with
 * the same request funnel as an extension. Four things differ, and all four
 * are handled by `wallet.ts`, not here:
 *   - `enable()` replaces `eth_requestAccounts`: it is the call that pairs;
 *   - "a provider exists" is NOT "a session exists". A provider whose pairing
 *     was deleted on the phone (or expired) cannot serve any account-bound
 *     method, so every caller must ask `hasWalletConnectSession()` and never
 *     infer it;
 *   - the approved chain set is frozen when the session is paired;
 *   - every request is a relay round-trip to a phone, so it needs a deadline.
 */

import { loadChains, loadNetworkChainIds, walletRpcMap } from "./chains";
import { getWalletConnectProjectId } from "./runtimeConfig";
import type { Eip1193Provider } from "./wallet";

/** Synthetic EIP-6963 rdns, namespaced under the app so no extension can announce it and hijack the pick. */
export const WALLETCONNECT_RDNS = "aile.walletconnect";

export const WALLETCONNECT_NAME = "WalletConnect";
export const WALLETCONNECT_HINT = "Scan with your phone";

/** Inline mark: the bundle ships no external images. */
export const WALLETCONNECT_ICON =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 32 32'%3E%3Crect width='32' height='32' rx='8' fill='%233b99fc'/%3E%3Cpath d='M9.6 12.6c3.5-3.5 9.3-3.5 12.8 0l.4.4a.44.44 0 0 1 0 .63l-1.44 1.41a.23.23 0 0 1-.32 0l-.58-.57a6.3 6.3 0 0 0-8.82 0l-.62.61a.23.23 0 0 1-.32 0L9.26 13.7a.44.44 0 0 1 0-.63zm15.8 2.96 1.28 1.26a.44.44 0 0 1 0 .63l-5.78 5.67a.46.46 0 0 1-.64 0l-4.1-4.03a.11.11 0 0 0-.16 0l-4.1 4.03a.46.46 0 0 1-.64 0L5.48 17.45a.44.44 0 0 1 0-.63l1.28-1.26a.46.46 0 0 1 .64 0l4.1 4.02c.05.05.12.05.16 0l4.1-4.02a.46.46 0 0 1 .64 0l4.1 4.02c.05.05.12.05.16 0l4.1-4.02a.46.46 0 0 1 .64 0z' fill='%23fff'/%3E%3C/svg%3E";

/**
 * Synthetic codes for the WalletConnect outcomes that have no EIP-1193
 * equivalent. Each is carried on a `WalletError` whose message is already
 * finished English, and `explainWalletError` renders it verbatim.
 */
export const WALLETCONNECT_UNAVAILABLE = "WALLETCONNECT_UNAVAILABLE";
export const WALLETCONNECT_TIMEOUT = "WALLETCONNECT_TIMEOUT";
export const WALLETCONNECT_EXPIRED = "WALLETCONNECT_EXPIRED";
/**
 * The paired session cannot carry what the page asked for: it holds no
 * account on that network (the wallet app approved a subset of the networks),
 * or grants no transaction there. Not a fault: the operator fixes it by
 * connecting again (or with another wallet), so it is never reported.
 */
export const WALLETCONNECT_RECONNECT = "WALLETCONNECT_RECONNECT";

/** WalletConnect v2's JSON-RPC code for "the user rejected this". */
export const WALLETCONNECT_USER_REJECTED = 5000;

/**
 * Thrown by `initWalletConnect()` when it cannot hand back a provider, for one
 * of two unrelated reasons that callers must NOT treat alike:
 *   - "unconfigured": this deployment published no project id (or no
 *     network). Permanent until the descriptor changes, so a connect that only
 *     got here because of the pick may forget the pick;
 *   - "closed": a teardown (Cancel, another wallet, sign-out) overtook the
 *     init. Transient and caused by the operator, so it is a cancellation.
 */
export class WalletConnectUnavailable extends Error {
  readonly code = WALLETCONNECT_UNAVAILABLE;
  readonly reason: "unconfigured" | "closed";

  constructor(message: string, reason: "unconfigured" | "closed" = "unconfigured") {
    super(message);
    this.name = "WalletConnectUnavailable";
    this.reason = reason;
  }
}

/** The bits of `EthereumProvider` this app uses, on top of EIP-1193. */
export interface WalletConnectProvider extends Eip1193Provider {
  /** connect-if-needed + return accounts. Not `eth_requestAccounts`. */
  enable(): Promise<string[]>;
  disconnect(): Promise<void>;
  /** Undefined until a session exists (restored from storage or freshly paired). */
  readonly session: unknown;
  /**
   * The QR sheet. The library closes it only from `connect()`'s own `finally`,
   * so a cancel that does not go through the sheet leaves it on screen: see
   * `closeWalletConnectModal()`. Optional: most test fakes have none.
   */
  readonly modal?: AppKitModal | undefined;
  /**
   * The chain every request is sent for: `EthereumProvider.request` relays
   * with `"eip155:" + this.chainId`, so this (not the session, not the page's
   * intent) decides which chain a signature or a transaction goes out on. The
   * real provider always carries a number; the test fakes may carry none. See
   * `holdOnNetworks`.
   */
  readonly chainId?: number | undefined;
  /** Approved accounts for the current chain, `[]` before pairing. Only the real provider carries it. */
  readonly accounts?: readonly string[] | undefined;
  /**
   * The UniversalProvider underneath. Its `connect` event is emitted from the
   * end of its own `pair()` only, so it is the one signal that a provider we
   * already dropped has paired anyway (see `closeWalletConnectModal`); its
   * `client` (the sign-client) is the one that says a session expired.
   * Optional: the test fakes have none.
   */
  readonly signer?:
    | {
        on?(event: "connect", listener: () => void): unknown;
        removeListener?(event: "connect", listener: () => void): unknown;
        client?: {
          on?(event: "session_expire", listener: (event: { topic?: unknown }) => void): unknown;
          off?(event: "session_expire", listener: (event: { topic?: unknown }) => void): unknown;
          removeListener?(event: "session_expire", listener: (event: { topic?: unknown }) => void): unknown;
        };
      }
    | undefined;
}

/**
 * The slice of the AppKit 1.8.19 instance (`provider.modal`, typed `any` by
 * the package) this module touches. `subscribeState` watches MODULE-GLOBAL
 * state, shared by every AppKit instance created in this tab. Both optional:
 * most test fakes carry `close` alone.
 */
export interface AppKitModal {
  close(): unknown;
  updateOptions?(options: Record<string, unknown>): void;
  subscribeState?(callback: (state: { open?: boolean }) => void): () => void;
}

/**
 * What a session is created with: the project, the networks it is proposed
 * for, and their public read endpoints (`walletRpcMap`). Without an endpoint,
 * a request outside every WalletConnect method namespace goes to Reown's
 * shared API on the project id.
 */
export interface WalletConnectSetup {
  projectId: string;
  chainIds: [number, ...number[]];
  rpcMap: Record<number, string>;
}

/** Seam for tests: jsdom has no usable WebSocket or crypto for the real package. */
export type WalletConnectFactory = (opts: WalletConnectSetup) => Promise<WalletConnectProvider>;

/**
 * Exactly what `EthereumProvider.init` is called with, lifted out of
 * `defaultFactory` so the session proposal can be asserted in a test (the
 * package itself is never loaded in jsdom).
 *
 * The networks: every one the Maker portal serves (`loadNetworkChainIds`),
 * the boot descriptor's first. The operator signs in on one and sends owner
 * calls on any, so the session must hold an account on each. Proposing them
 * does not keep the session on them: a wallet may approve more than it was
 * offered (MetaMask Mobile adds its own networks), so `holdOnNetworks` moves
 * the provider back whenever it lands on any other chain, and
 * `ensureWalletConnectChain` checks again before every request.
 *
 * Only optional namespaces: a wallet that cannot satisfy a required one
 * refuses the pairing outright, so one that approved a subset is checked
 * after pairing instead (`sessionGrantsSigning`, `sessionGrantsMethods`).
 */
export function walletConnectInitOptions({ projectId, chainIds, rpcMap }: WalletConnectSetup) {
  const origin = window.location.origin;
  return {
    projectId,
    optionalChains: chainIds,
    // The portal signs the sign-in message, moves the wallet to a call's
    // network and sends the owner calls it prepared. Asking for methods this
    // app never calls only widens what the wallet's approval sheet claims we
    // may do.
    optionalMethods: ["personal_sign", "wallet_switchEthereumChain", "eth_sendTransaction"],
    rpcMap,
    showQrModal: true,
    metadata: {
      name: "AileFX Maker portal",
      description: "The console of AileFX's market makers",
      // Must equal the live origin: Reown's verify service cross-checks it, and
      // wallets show a red "domain mismatch" warning when it differs.
      url: origin,
      // No icon: this repo ships no static images, and a URL that 404s renders
      // as a broken tile in the wallet's approval sheet.
      icons: [],
    },
  };
}

/**
 * Turns AppKit's `allowUnsupportedChain` on for `p`'s sheet, after EVERY init
 * (an orphaned one's included: each AppKit construction resets the global
 * option). Without it, a wallet that re-announces a chain we did not offer
 * makes AppKit open its "unsupported network" view, which under the
 * provider's manual control is the QR sheet again, over a signed-in page.
 * `guardModal` is the backstop for any other way to open it.
 */
function allowUnsupportedChain(p: WalletConnectProvider): void {
  try {
    p.modal?.updateOptions?.({ allowUnsupportedChain: true });
  } catch {
    // An AppKit without it: the modal guard still closes a stray sheet.
  }
}

const defaultFactory: WalletConnectFactory = async (setup) => {
  const { EthereumProvider } = await import("@walletconnect/ethereum-provider");
  const provider = await EthereumProvider.init(walletConnectInitOptions(setup));
  // The repo's `Eip1193Provider` types listeners as `(...args: never[]) => void`,
  // which no declared provider class satisfies; the narrowing is kept here, in
  // one place, rather than loosening every extension call site.
  return provider as unknown as WalletConnectProvider;
};

let factory: WalletConnectFactory = defaultFactory;
let provider: WalletConnectProvider | null = null;
let initInFlight: Promise<WalletConnectProvider> | null = null;
/**
 * Bumped by every teardown. `initWalletConnect()` captures it before awaiting
 * the factory and refuses to install a provider whose generation is stale, so
 * a teardown that lands while an init is in flight is not undone by it.
 */
let generation = 0;
const disconnectListeners = new Set<() => void>();

function notifyDisconnected(): void {
  for (const listener of [...disconnectListeners]) listener();
}

/**
 * Fires when the WALLET ends the pairing (the operator killing the session on
 * the phone) or the session expires. Registered on the WalletConnect provider
 * itself: an extension's EIP-1193 `disconnect` means "lost its RPC endpoint",
 * a network blip, never this.
 */
export function onWalletConnectDisconnected(handler: () => void): () => void {
  disconnectListeners.add(handler);
  return () => {
    disconnectListeners.delete(handler);
  };
}

// ---- holding the provider on the Maker portal's networks -----------------------

/** The networks each provider was proposed for, the one the app last asked for, and the listener. */
interface Hold {
  chainIds: readonly number[];
  current: number;
  listener: (raw: unknown) => void;
}

const holds = new WeakMap<WalletConnectProvider, Hold>();

function parseChainId(raw: unknown): number | null {
  if (typeof raw === "number") return raw;
  if (typeof raw !== "string") return null;
  const parsed = raw.startsWith("0x") ? Number.parseInt(raw, 16) : Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
}

function sessionNamespaces(p: WalletConnectProvider): Record<string, { accounts?: unknown; methods?: unknown }> | null {
  const namespaces = (p.session as { namespaces?: Record<string, { accounts?: unknown; methods?: unknown }> } | undefined | null)?.namespaces;
  return namespaces === undefined || namespaces === null || typeof namespaces !== "object" ? null : namespaces;
}

/**
 * Does the paired session hold an account on `chainId`? Mirrors the library's
 * own test (universal-provider 2.25.0 `isChainApproved`: the chains of every
 * eip155 namespace's accounts), so "we think it is approved" never disagrees
 * with it. An unreadable session counts as not approved.
 */
function sessionApprovesChain(p: WalletConnectProvider, chainId: number): boolean {
  const namespaces = sessionNamespaces(p);
  if (namespaces === null) return false;
  const prefix = `eip155:${chainId}:`;
  return Object.entries(namespaces).some(
    ([key, value]) => key.includes("eip155") && Array.isArray(value?.accounts) && value.accounts.some((a) => typeof a === "string" && a.startsWith(prefix)),
  );
}

/**
 * The same question with "cannot tell" kept apart: null when the session's
 * accounts are unreadable (a test fake, or the library changed under us).
 * `switchToChain` refuses a network only on a readable "no".
 */
export function walletConnectApproves(p: WalletConnectProvider, chainId: number): boolean | null {
  const namespaces = sessionNamespaces(p);
  if (namespaces === null) return null;
  const readable = Object.entries(namespaces).some(([key, value]) => key.includes("eip155") && Array.isArray(value?.accounts));
  return readable ? sessionApprovesChain(p, chainId) : null;
}

/**
 * Moves the provider to `chainId` without asking the wallet anything. An
 * approved chain never leaves the browser (universal-provider 2.25.0
 * `handleSwitchChain` sets its default chain and returns), which is why the
 * approval is checked first and `null` returned without it: an unapproved one
 * would be relayed to the wallet as a real prompt.
 */
function switchLocally(p: WalletConnectProvider, chainId: number): Promise<unknown> | null {
  if (!sessionApprovesChain(p, chainId)) return null;
  return p.request({ method: "wallet_switchEthereumChain", params: [{ chainId: `0x${chainId.toString(16)}` }] });
}

/** Where to hold `p`: the network the app last asked for, else the first offered one the session approves. */
function holdTarget(p: WalletConnectProvider, hold: Hold): number | null {
  if (sessionApprovesChain(p, hold.current)) return hold.current;
  const other = hold.chainIds.find((id) => sessionApprovesChain(p, id));
  if (other === undefined) return null;
  hold.current = other;
  return other;
}

/**
 * Keeps a live provider on the Maker portal's networks.
 *
 * Three ways it leaves them: `connect()` starts it on the first approved
 * account's chain (Ethereum mainnet for MetaMask Mobile), a `chainChanged`
 * session event from the wallet moves it to any approved chain, and a
 * restored session starts on whatever chain was persisted last. Each surfaces
 * as the provider's `chainChanged`. A move onto another offered network is
 * followed, as an extension's network switch is (every page checks the
 * network before it sends, and `ensureWalletConnectChain` moves a transaction
 * to its own); a move anywhere else is undone at once, locally, inside the
 * library's emit, so no request can go out in between.
 */
function holdOnNetworks(p: WalletConnectProvider, chainIds: [number, ...number[]]): void {
  const hold: Hold = {
    chainIds,
    current: chainIds[0],
    listener: (raw: unknown): void => {
      // Only for the provider the app is using; a dropped one is being hung up.
      if (provider !== p) return;
      const now = parseChainId(raw);
      if (now === null) return;
      if (hold.chainIds.includes(now)) {
        hold.current = now;
        return;
      }
      const back = holdTarget(p, hold);
      if (back !== null) void switchLocally(p, back)?.catch(() => undefined);
    },
  };
  holds.set(p, hold);
  p.on?.("chainChanged", hold.listener as (...args: never[]) => void);
  // A restored session: its persisted chain was read before we listened. The
  // switch is made even when it already looks right, so the provider's two
  // chain ids (the relayed requests', and the local answers') move together.
  if (hasWalletConnectSession(p)) {
    const onOffered = typeof p.chainId === "number" && chainIds.includes(p.chainId) && sessionApprovesChain(p, p.chainId) ? p.chainId : null;
    if (onOffered !== null) hold.current = onOffered;
    const target = onOffered ?? holdTarget(p, hold);
    if (target !== null) void switchLocally(p, target)?.catch(() => undefined);
  }
}

function releaseHold(p: WalletConnectProvider): void {
  const hold = holds.get(p);
  if (hold === undefined) return;
  p.removeListener?.("chainChanged", hold.listener as (...args: never[]) => void);
  holds.delete(p);
}

/** The networks `p` was proposed for (never empty), or null when it was never held (a fake built outside `initWalletConnect`). */
export function walletConnectNetworks(p: WalletConnectProvider): readonly number[] | null {
  return holds.get(p)?.chainIds ?? null;
}

/**
 * The network a session-level check (the sign-in grant) is made for: the one
 * the provider is on when it is offered, else the one it is held on.
 */
export function walletConnectHome(p: WalletConnectProvider): number | null {
  const hold = holds.get(p);
  if (hold === undefined) return null;
  return typeof p.chainId === "number" && hold.chainIds.includes(p.chainId) ? p.chainId : hold.current;
}

/** Records the network the app asked for (a switch that succeeded), so the hold keeps it there. */
export function walletConnectHoldOn(p: WalletConnectProvider, chainId: number): void {
  const hold = holds.get(p);
  if (hold !== undefined && hold.chainIds.includes(chainId)) hold.current = chainId;
}

/**
 * True when `p` may send a request now, never prompting:
 *   - with `want` (a transaction for that network): it is on `want`, or was
 *     just moved there locally; false when the session holds no account there;
 *   - without: it is on one of the offered networks, or was just moved back to
 *     one; false when it is elsewhere and cannot be moved back. The caller then
 *     refuses rather than sign or send for another chain.
 *
 * A provider with no numeric `chainId` (the test fakes) or never held passes:
 * there is no chain state to check. The real `EthereumProvider` always has one.
 */
export async function ensureWalletConnectChain(p: WalletConnectProvider, want?: number): Promise<boolean> {
  const hold = holds.get(p);
  if (hold === undefined || typeof p.chainId !== "number") return true;
  if (want !== undefined) {
    if (p.chainId === want) return true;
    if (!hold.chainIds.includes(want)) return false;
    const switching = switchLocally(p, want);
    if (switching !== null) await switching.catch(() => undefined);
    if (p.chainId !== want) return false;
    hold.current = want;
    return true;
  }
  if (hold.chainIds.includes(p.chainId)) return true;
  const back = holdTarget(p, hold);
  if (back === null) return false;
  await switchLocally(p, back)?.catch(() => undefined);
  return typeof p.chainId === "number" && hold.chainIds.includes(p.chainId);
}

// ---- the QR sheet ------------------------------------------------------------------

/**
 * `enable()` calls in flight, across every provider. The QR sheet belongs on
 * screen only while one is: `EthereumProvider.connect()` opens it and closes it
 * again from its own `finally`, both inside `enable()`.
 */
let pairingDepth = 0;
/** Unsubscribes the one modal guard; see `guardModal`. */
let modalGuardOff: (() => void) | null = null;

/**
 * Closes the QR sheet whenever it opens while no pairing is in flight: under
 * the provider's manual control, ANY open of AppKit's sheet (an unsupported
 * network, a stray `open()`) shows the QR code over whatever page the operator
 * is on. It only ever closes, so it cannot fight a real pairing (depth > 0
 * throughout), Cancel, or itself. Exactly one, dropped on teardown: the state
 * it watches is global, so one left on a dropped provider would act on the
 * next provider's sheet too.
 */
function guardModal(p: WalletConnectProvider): void {
  const modal = p.modal;
  modalGuardOff?.();
  modalGuardOff = null;
  if (modal?.subscribeState === undefined) return;
  try {
    modalGuardOff = modal.subscribeState((state) => {
      if (state.open !== true || pairingDepth > 0) return;
      try {
        void Promise.resolve(modal.close()).catch(() => undefined);
      } catch {
        // Nothing to close.
      }
    });
  } catch {
    modalGuardOff = null;
  }
}

function unguardModal(): void {
  modalGuardOff?.();
  modalGuardOff = null;
}

/**
 * The only way wallet.ts may call `enable()`: it marks a pairing as in flight
 * for the modal guard, which would otherwise close the QR sheet the moment it
 * opened.
 */
export async function enableWalletConnect(p: WalletConnectProvider): Promise<string[]> {
  pairingDepth += 1;
  try {
    return await p.enable();
  } finally {
    pairingDepth -= 1;
  }
}

/** Tests only: install a fake provider factory. */
export function setWalletConnectFactory(next: WalletConnectFactory | null): void {
  factory = next ?? defaultFactory;
}

/** The provider, or null when nothing has been created yet (or it was dropped). */
export function getWalletConnectProvider(): WalletConnectProvider | null {
  return provider;
}

/**
 * True once a session exists (restored from storage or freshly paired) and
 * has not expired. A session past its `expiry` (seconds) is one the
 * sign-client has deleted or is about to: it can serve nothing.
 */
export function hasWalletConnectSession(p: WalletConnectProvider): boolean {
  const session = p.session as { expiry?: unknown } | undefined | null;
  if (session === undefined || session === null) return false;
  return !(typeof session.expiry === "number" && session.expiry * 1000 <= Date.now());
}

/** The session's topic, to match a sign-client event to it. */
function sessionTopic(p: WalletConnectProvider): unknown {
  return (p.session as { topic?: unknown } | undefined | null)?.topic;
}

/** The expiry listener of each adopted provider, so the teardown can remove it. */
const expiryListeners = new WeakMap<WalletConnectProvider, (event: { topic?: unknown }) => void>();

/**
 * A session that expires while the page is open: the sign-client deletes it
 * and emits `session_expire`, which the provider above it does not pass on,
 * so nothing would tell the page. Treated as the phone ending the pairing.
 */
function watchExpiry(p: WalletConnectProvider): void {
  const client = p.signer?.client;
  if (client?.on === undefined) return;
  const listener = (event: { topic?: unknown }): void => {
    if (provider !== p) return;
    const topic = sessionTopic(p);
    if (topic === undefined || event?.topic === topic) notifyDisconnected();
  };
  expiryListeners.set(p, listener);
  client.on("session_expire", listener);
}

function unwatchExpiry(p: WalletConnectProvider): void {
  const listener = expiryListeners.get(p);
  if (listener === undefined) return;
  expiryListeners.delete(p);
  const client = p.signer?.client;
  if (client?.off !== undefined) client.off("session_expire", listener);
  else client?.removeListener?.("session_expire", listener);
}

/**
 * Creates (once) the provider for this deployment. `init()` rehydrates a
 * session left in its own IndexedDB store, with no prompt, so the caller must
 * check `hasWalletConnectSession()` rather than assume a fresh pairing.
 *
 * Not `async`: the singleton guard and the `initInFlight` assignment must
 * happen in one synchronous run, or two callers race past the guard.
 */
export function initWalletConnect(): Promise<WalletConnectProvider> {
  if (provider !== null) return Promise.resolve(provider);
  if (initInFlight !== null) return initInFlight;
  const mine = generation;
  const pending = (async () => {
    const projectId = getWalletConnectProjectId();
    if (projectId === null) {
      throw new WalletConnectUnavailable("WalletConnect is not available here.");
    }
    const chains = await loadChains();
    if (mine !== generation) throw new WalletConnectUnavailable("WalletConnect session was closed", "closed");
    const [first, ...rest] = await loadNetworkChainIds(chains);
    if (first === undefined) {
      // No network names a chain id: none stands in.
      throw new WalletConnectUnavailable("WalletConnect is not available: no network is published yet.");
    }
    const chainIds: [number, ...number[]] = [first, ...rest];
    if (mine !== generation) throw new WalletConnectUnavailable("WalletConnect session was closed", "closed");
    const created = await factory({ projectId, chainIds, rpcMap: walletRpcMap(chains, chainIds) });
    // Before the generation check: an orphaned init has still reset the
    // global AppKit options.
    allowUnsupportedChain(created);
    if (mine !== generation) {
      // Torn down while we were initialising. Drop it rather than adopt it,
      // and hang up on the relay so the phone stops showing a live session.
      void created.disconnect().catch(() => undefined);
      throw new WalletConnectUnavailable("WalletConnect session was closed", "closed");
    }
    provider = created;
    // Only for the provider we adopt. All are undone by the teardown.
    holdOnNetworks(created, chainIds);
    guardModal(created);
    watchExpiry(created);
    // `session_delete` only: one wallet-side delete makes the library emit
    // BOTH `session_delete` and `disconnect`, and `disconnect` from nowhere
    // else, so listening to both would run every handler twice.
    created.on?.("session_delete", notifyDisconnected);
    return created;
  })();
  const settled: Promise<WalletConnectProvider> = pending.finally(() => {
    if (initInFlight === settled) initInFlight = null;
  });
  initInFlight = settled;
  // The orphan branch above rejects a promise nobody is awaiting any more.
  void settled.catch(() => undefined);
  return settled;
}

/**
 * Set when a teardown could not reach the relay (see `disconnectWalletConnect`).
 * localStorage, not a module variable: it must survive the reload or the
 * closed tab that would otherwise rehydrate the abandoned pairing.
 */
const UNCLEAN_KEY = storageKeys.walletConnectUnclean;

function markUnclean(on: boolean): void {
  try {
    if (on) localStorage.setItem(UNCLEAN_KEY, "1");
    else localStorage.removeItem(UNCLEAN_KEY);
  } catch {
    // No storage at all: then nothing was persisted for us to distrust.
  }
}

/** True when the last teardown may have left the pairing alive on the relay. */
export function walletConnectTeardownUnclean(): boolean {
  try {
    return localStorage.getItem(UNCLEAN_KEY) !== null;
  } catch {
    return false;
  }
}

/** Cleared once a pairing this browser owns has been established. */
export function clearWalletConnectUnclean(): void {
  markUnclean(false);
}

/**
 * sign-client 2.25.0's session proposal lives five minutes; plus a margin. At
 * expiry the proposal is deleted, so a dropped provider can no longer pair.
 */
const ORPHAN_WATCH_MS = timing.wallet.orphanWatchMs;

/** One hang-up per dropped provider: a second `disconnect()` on the real provider throws. */
const hangUps = new WeakMap<WalletConnectProvider, Promise<void>>();
/** Hang-ups still on the relay; the next connect waits for them. */
const pendingHangUps = new Set<Promise<void>>();
/** Stops for the armed watchers, so a test reset leaves no timer behind. */
const orphanWatchers = new Set<() => void>();

/**
 * Ends the session of a provider the singleton no longer holds. Never clears
 * the unclean flag (the flag may be about another pairing), but a failure
 * sets it: the library publishes the delete before it deletes its local
 * record, so a hang-up that cannot reach the relay leaves the session in the
 * storage every provider shares, where the next `init()` rehydrates it.
 */
export function hangUpOrphan(p: WalletConnectProvider): Promise<void> {
  const existing = hangUps.get(p);
  if (existing !== undefined) return existing;
  const run = (async () => {
    if (!hasWalletConnectSession(p)) return;
    await p.disconnect().catch(() => undefined);
    if (hasWalletConnectSession(p)) markUnclean(true);
  })();
  hangUps.set(p, run);
  pendingHangUps.add(run);
  void run.finally(() => pendingHangUps.delete(run));
  return run;
}

/**
 * Null when no orphan hang-up is in flight (so a caller with nothing to wait
 * for stays synchronous), otherwise a promise that resolves once every one
 * has settled. Never rejects. wallet.ts waits on it before `init()` (an
 * orphan's session sits in the shared storage until its delete lands) and
 * before `enable()` (an orphan's hang-up expires every pending pairing in the
 * shared core, a new QR code's included).
 */
export function orphanHangUpsSettled(): Promise<void> | null {
  if (pendingHangUps.size === 0) return null;
  return Promise.allSettled([...pendingHangUps]).then(() => undefined);
}

/** Teardowns so far: a caller that awaits before `initWalletConnect()` compares it across the await. */
export function walletConnectGeneration(): number {
  return generation;
}

/**
 * Hangs up on `p` if it pairs after we dropped it. Bound to that one object:
 * its signer's `connect` is emitted by its own `pair()` only, so this can never
 * end the session of a later attempt.
 */
function watchForLateSession(p: WalletConnectProvider): void {
  const signer = p.signer;
  if (signer?.on === undefined) return;
  const onConnect = (): void => {
    stop();
    void hangUpOrphan(p);
  };
  const timer = setTimeout(stop, ORPHAN_WATCH_MS);
  function stop(): void {
    clearTimeout(timer);
    signer?.removeListener?.("connect", onConnect);
    orphanWatchers.delete(stop);
  }
  orphanWatchers.add(stop);
  signer.on("connect", onConnect);
}

/**
 * The operator backed out before the pairing completed. Closing the sheet is
 * what unwinds OUR side of the attempt, and the only thing that does:
 * `enable()` has no deadline, `disconnect()` is a no-op without a session, and
 * the library closes its own sheet only from `connect()`'s `finally`. The
 * provider then rejects `connect()` with "Connection request reset", which
 * `isWalletConnectReset()` maps onto a cancellation.
 *
 * It does NOT stop the pairing (the library's abort is a no-op in 2.25.0): a
 * phone that approves within the proposal's five minutes gives the dropped
 * provider a live session. So Cancel also drops the singleton (wallet.ts
 * `cancelConnect`), whose teardown arms `watchForLateSession`, and the next
 * click waits for that hang-up before its own `init()` can rehydrate it.
 */
export function closeWalletConnectModal(): void {
  try {
    void Promise.resolve(provider?.modal?.close()).catch(() => undefined);
  } catch {
    // No sheet (init never finished): nothing to close.
  }
}

/**
 * Methods the sign-in cannot work without. The proposal carries no required
 * namespace, so a wallet is free to approve a subset; an ungranted method then
 * fails deep inside the sign-client. Read from the SESSION, never from the
 * provider's merged namespace, which always contains what we asked for.
 */
const REQUIRED_SESSION_METHODS = ["personal_sign"] as const;

/** True when the paired session can sign the sign-in message on `chainId`. */
export function sessionGrantsSigning(p: WalletConnectProvider, chainId: number): boolean {
  return sessionGrantsMethods(p, chainId, REQUIRED_SESSION_METHODS);
}

/**
 * True when the paired session grants every one of `methods` on `chainId`,
 * scoped per chain the way the sign-client checks a request: only the entries
 * whose accounts include that chain contribute (the prefix keeps its trailing
 * colon, so 8453 never matches 84532). An unreadable session shape counts as
 * granted on purpose: refusing every sign-in because the library changed
 * under us would be a self-inflicted outage. `sendTransaction` asks it about
 * `eth_sendTransaction` before every owner call.
 */
export function sessionGrantsMethods(p: WalletConnectProvider, chainId: number, methods: readonly string[]): boolean {
  const namespaces = sessionNamespaces(p);
  if (namespaces === null) return true;
  const prefix = `eip155:${chainId}:`;
  const granted = new Set<string>();
  let readable = false;
  for (const [key, value] of Object.entries(namespaces)) {
    // eip155 is the only namespace this app speaks; wallets key it either
    // per namespace ("eip155") or per chain ("eip155:8453").
    if (key !== "eip155" && !key.startsWith("eip155:")) continue;
    const listed = value?.methods;
    if (!Array.isArray(listed)) continue;
    // Readable before the chain filter: entries that all serve other chains
    // are a readable answer ("not here"), not an unknown shape.
    readable = true;
    const accounts = value?.accounts;
    if (Array.isArray(accounts) && !accounts.some((a) => typeof a === "string" && a.startsWith(prefix))) continue;
    for (const method of listed) if (typeof method === "string") granted.add(method);
  }
  if (!readable) return true;
  return methods.every((method) => granted.has(method));
}

/** The sign-client's local refusal of a method the session does not grant. */
export function isWalletConnectMethodRefusal(err: unknown): boolean {
  if (err === null || typeof err !== "object") return false;
  const message = (err as { message?: unknown }).message;
  return typeof message === "string" && /Missing or invalid\. request\(\) method/.test(message);
}

/**
 * Tears the pairing down and drops the singleton. Never rejects: a sign-out or
 * a change of wallet must not depend on a relay round-trip, and the library
 * throws outright when asked to disconnect with no session.
 */
export async function disconnectWalletConnect(): Promise<void> {
  generation += 1; // orphans any in-flight init
  const current = provider;
  provider = null;
  initInFlight = null;
  if (current === null) return;
  current.removeListener?.("session_delete", notifyDisconnected);
  releaseHold(current);
  unwatchExpiry(current);
  unguardModal();
  if (!hasWalletConnectSession(current)) {
    // Possibly mid-pairing (Cancel, or another wallet picked while the QR was
    // up): the pairing keeps running after we drop it.
    watchForLateSession(current);
    return;
  }
  try {
    await current.disconnect();
    markUnclean(false);
  } catch {
    // Gone as far as this browser is concerned, but it may well be alive on
    // the relay and rehydrated by the next `init()`: remember that, so a later
    // connect hangs up on it instead of resuming someone else's wallet.
    markUnclean(true);
  }
}

/**
 * The operator closed the QR sheet, or declined the proposal in the wallet
 * app: an outcome, not a fault. Matched on exact signals only: a keyword match
 * such as /closed|cancel/i would also swallow "WebSocket connection closed
 * abnormally", a dead relay, and file the failure this integration is most
 * likely to hit as "the operator changed their mind".
 */
export function isWalletConnectRejection(err: unknown): boolean {
  if (err === null || typeof err !== "object") return false;
  if ((err as { code?: unknown }).code === WALLETCONNECT_USER_REJECTED) return true;
  return isWalletConnectReset(err);
}

/**
 * The QR sheet was closed with no session (its X, or Cancel closing it): the
 * one rejection no wallet took part in, so the UI says nothing rather than
 * "refused".
 */
export function isWalletConnectReset(err: unknown): boolean {
  if (err === null || typeof err !== "object") return false;
  const message = (err as { message?: unknown }).message;
  return typeof message === "string" && message.startsWith("Connection request reset");
}

/** The session proposal timed out before the wallet answered. */
export function isWalletConnectExpiry(err: unknown): boolean {
  if (err === null || typeof err !== "object") return false;
  const message = (err as { message?: unknown }).message;
  return typeof message === "string" && /^Proposal expired/i.test(message);
}

/** Drops provider, factory and listeners. Tests only (`resetWalletDiscovery()`). */
export function resetWalletConnect(): void {
  generation += 1;
  provider = null;
  initInFlight = null;
  factory = defaultFactory;
  disconnectListeners.clear();
  for (const stop of [...orphanWatchers]) stop();
  pendingHangUps.clear();
  // Not `pairingDepth`: an `enable()` still settling decrements it later.
  unguardModal();
  markUnclean(false);
}
